import React, { useContext, useEffect, useState } from "react";
import { Grid, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";

import { CURRENCIES, taxNames, taxTypes, feeTypes } from "../../constants";

import { FleetContext, FleetSettingsContext } from "../../context";
import { GridRow, TitledSection, CollapsibleSectionToggle } from "../../components/FormElements";
import { ActionIcon, EditFormField } from "./FleetSettingsFormElements/Billing";
import formatNumber from "../../util/strings/formatNumber";

const SettingsBillingEdit = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { activeFleet } = useContext(FleetContext);
  const {
    billing,
    setBillingSettingsForm,
    activeRatePlan,
    fetchRatePlan,
    fetchFineTypes,
    fineTypes,
  } = useContext(FleetSettingsContext);

  const [minFareEnabled, setMinFareEnabled] = useState(false);
  const [minFareThreshold, setMinFareThreshold] = useState("");
  const [minFareErrorMessage, setMinFareErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [paymentProviders, setPaymentProviders] = useState({
    stripe: {
      commonEnLookup: "StripeDefault",
      enabled: false,
    },
    braintree: {
      commonEnLookup: "PayPal",
      enabled: false,
    },
  });

  useEffect(() => {
    fetchFineTypes();
  }, []);

  const handleFetchRatePlan = async () => {
    await fetchRatePlan(activeFleet?.rate_plan_id);
  };

  useEffect(() => {
    if (activeFleet) {
      handleFetchRatePlan(activeFleet.rate_plan_id);
    }
  }, [activeFleet]);

  useEffect(() => {
    if (!activeRatePlan) setLoading(true);
    else setLoading(false);
  }, [activeRatePlan]);

  useEffect(() => {
    if (activeRatePlan) {
      const newBillingForm = { ...activeRatePlan };

      setMinFareEnabled(
        newBillingForm.min_fleet_charge_amount !== null &&
          newBillingForm.min_fleet_charge_minutes !== null,
      );

      if (
        newBillingForm.rate_plan_fine_types === undefined ||
        newBillingForm.rate_plan_fine_types === null
      ) {
        newBillingForm.rate_plan_fine_types = [];
      }

      setBillingSettingsForm(newBillingForm);
    }
  }, [activeRatePlan, fineTypes]);

  useEffect(() => {
    if (minFareEnabled) {
      // Threshold formula provided by Product team:
      const threshold =
        (billing?.min_fleet_charge_amount - billing?.flat_fee) / billing?.time_unit_fee;
      setMinFareThreshold(threshold);
      setBillingSettingsForm({ ...billing, min_fleet_charge_minutes: threshold });
      const errorMessage = getMinThresholdErrorText(threshold);
      setMinFareErrorMessage(errorMessage);
    }
  }, [billing.min_fleet_charge_amount, billing.flat_fee, billing.time_unit_fee]);

  const handleMinFareToggle = () => {
    const amount = minFareEnabled ? null : "0";
    const minutes = minFareEnabled ? null : "0";
    setBillingSettingsForm({
      ...billing,
      min_fleet_charge_amount: amount,
      min_fleet_charge_minutes: minutes,
    });
    setMinFareEnabled(!minFareEnabled);
  };

  const checkPaymentMethods = (paymentMethodName) =>
    activeRatePlan?.payment_providers?.find(
      (paymentProvider) => paymentProvider.provider === paymentMethodName,
    );

  // Exclusively checks for PayPal aka Braintree
  useEffect(() => {
    const updatedProviders = Object.keys(paymentProviders).reduce((acc, providerName) => {
      acc[providerName] = {
        ...paymentProviders[providerName],
        enabled: Boolean(checkPaymentMethods(providerName)),
      };
      return acc;
    }, {});

    setPaymentProviders(updatedProviders);
  }, [activeRatePlan?.payment_providers]);

  // Currently only handles PayPal aka Braintree but is written to take a variable in the event we expand this for other PSPs
  // Ideally in the future the data structure will change to make this method unnecessary

  const handlePaymentProviderToggle = (providerName) => {
    if (paymentProviders[providerName].enabled) {
      setBillingSettingsForm({
        ...billing,
        payment_providers: billing.payment_providers.filter((psp) => psp.provider !== providerName),
      });
      setPaymentProviders({
        ...paymentProviders,
        [providerName]: {
          ...paymentProviders[providerName],
          enabled: false,
        },
      });
    } else {
      setBillingSettingsForm({
        ...billing,
        payment_providers: [
          ...billing.payment_providers,
          {
            default: false,
            provider: "braintree",
          },
        ],
      });
      setPaymentProviders({
        ...paymentProviders,
        [providerName]: {
          ...paymentProviders[providerName],
          enabled: true,
        },
      });
    }
  };

  const getMinThresholdErrorText = (value) => {
    if (value && value < 0) {
      return t("Fleet.Settings.BillingEdit.Errors.NegativeMinFare");
    }
    if (value && Number(value) % 1 !== 0) {
      return t("Fleet.Settings.BillingEdit.Errors.NonIntegerMinFare");
    }
    return "";
  };

  const trimDecimal = (value) => {
    if (value % 1 === 0 || value !== Number(value)) return value;
    const splitVal = String(value).split(".");
    const [whole, decimal] = splitVal;
    if (!decimal) return whole;
    let trimmedVal = `${whole}.`;
    for (let i = 0; i < decimal.length; i++) {
      const curr = decimal[i];
      trimmedVal += curr;
      if (curr !== "0") break;
    }
    return trimmedVal;
  };

  const handleFineChange = (event, field, ratePlanFineType, fineTypeIndex) => {
    let selectedFineType;
    let amount;
    if (field === "amount") {
      selectedFineType = fineTypes.find((ft) => ft.id === ratePlanFineType.fine_type_id);
      amount = event.target.value;
    } else if (field === "fine_type") {
      selectedFineType = fineTypes.find((ft) => ft.id === event.target.value);
      amount = ratePlanFineType.amount;
    }
    const { id: typeId, name: typeName, is_revenue: isRevenue } = selectedFineType;
    const formattedAmount = formatNumber(amount, activeRatePlan.currency);
    const displayName = `${activeFleet.name} ${typeName} - ${formattedAmount}`;

    billing.rate_plan_fine_types[fineTypeIndex] = {
      id: ratePlanFineType.id,
      fine_type_id: typeId,
      fine_type_is_revenue: isRevenue,
      fine_type_name: typeName,
      name: displayName,
      amount,
    };
    setBillingSettingsForm({ ...billing });
  };

  return (
    <Grid container style={{ padding: theme.spacing(0, 2, 2, 2) }}>
      {/* RATES */}
      <TitledSection title={t("Fleet.Settings.Billing.Rates")}>
        <GridRow>
          <EditFormField type="select" field="currency" menuOptions={CURRENCIES} disabled />
        </GridRow>
        <GridRow>
          <EditFormField unit="currency" field="flat_fee" />
          <EditFormField unit="currency" field="time_unit_fee" />
        </GridRow>
        <GridRow>
          <CollapsibleSectionToggle
            label={t("Fleet.Settings.BillingEdit.MinFareEnabled")}
            checked={minFareEnabled}
            onToggle={handleMinFareToggle}
          >
            <GridRow>
              <EditFormField unit="currency" field="min_fleet_charge_amount" />
              <EditFormField
                field="min_fleet_charge_minutes"
                unit="minutes"
                disabled
                overrideTextFieldProps={{
                  value: trimDecimal(minFareThreshold),
                  error: Boolean(minFareErrorMessage),
                  helperText: minFareErrorMessage,
                }}
              />
            </GridRow>
          </CollapsibleSectionToggle>
        </GridRow>
      </TitledSection>

      {/* BONUSES */}
      <TitledSection title={t("Fleet.Settings.Billing.Bonuses")}>
        <GridRow>
          <EditFormField unit="currency" field="first_card_bonus" />
        </GridRow>
        {billing.auto_renew_options?.map((option, idx) => (
          <GridRow key={option.id}>
            <EditFormField
              unit="currency"
              field="auto_renew_options"
              nestedIndex={idx}
              nestedField="amount"
            />
            <EditFormField
              unit="currency"
              field="auto_renew_options"
              nestedIndex={idx}
              nestedField="bonus"
            />
          </GridRow>
        ))}
      </TitledSection>

      {/* TAX */}
      <TitledSection title={t("Fleet.Settings.Billing.tax")} showDivider={false}>
        {!billing?.tax || !Object.keys(billing?.tax).length ? (
          <ActionIcon action="add" field="tax" />
        ) : (
          <GridRow>
            <EditFormField type="select" field="tax" menuOptions={taxNames} nestedField="name" />
            <EditFormField unit="percentage" field="tax" nestedField="rate" />
            <EditFormField
              type="select"
              field="tax"
              nestedField="type"
              menuOptions={taxTypes}
              fieldSize={3}
            />
            <ActionIcon action="remove" field="tax" />
          </GridRow>
        )}
      </TitledSection>

      {/* FEES */}
      <TitledSection title={t("Fleet.Settings.Billing.fees")}>
        <ActionIcon action="add" field="fees" />
        {billing.fees.map((fee, idx) => (
          <GridRow key={fee.id}>
            <EditFormField field="fees" nestedIndex={idx} nestedField="name" />
            <EditFormField
              type="select"
              field="fees"
              nestedIndex={idx}
              nestedField="type"
              menuOptions={feeTypes}
            />
            <EditFormField
              unit={fee.type}
              field="fees"
              nestedIndex={idx}
              nestedField="amount"
              fieldSize={3}
            />
            <ActionIcon action="remove" field="fees" fieldId={fee.id} />
          </GridRow>
        ))}
      </TitledSection>

      {/* FINES */}
      <TitledSection title={t("Fleet.Settings.Billing.fines")}>
        <ActionIcon action="add" field="rate_plan_fine_types" />
        {billing.rate_plan_fine_types.map((ratePlanFineType, idx) => (
          <GridRow key={ratePlanFineType.id}>
            <EditFormField
              type="select"
              field="rate_plan_fine_types"
              nestedIndex={idx}
              nestedField="fine_type_id"
              menuOptions={fineTypes.map(({ id, name }) => ({
                label: name,
                value: id,
              }))}
              overrideSelectProps={{
                onChange: (ev) => handleFineChange(ev, "fine_type", ratePlanFineType, idx),
              }}
            />
            <EditFormField
              unit="currency"
              field="rate_plan_fine_types"
              nestedIndex={idx}
              nestedField="amount"
              fieldSize={3}
              overrideTextFieldProps={{
                onChange: (ev) => handleFineChange(ev, "amount", ratePlanFineType, idx),
              }}
            />
            <ActionIcon
              action="remove"
              field="rate_plan_fine_types"
              fieldId={ratePlanFineType.id}
            />
          </GridRow>
        ))}
      </TitledSection>

      {/* WALLET AND HOLDS */}
      <TitledSection title={t("Fleet.Settings.Billing.walletAndHolds")}>
        <GridRow>
          <EditFormField unit="currency" field="blocking_amount" />
        </GridRow>
        <GridRow>
          <EditFormField field="group_rides_hold_adjustment" type="number" />
          <EditFormField
            disabled
            field="group_rides_hold_amount"
            overrideTextFieldProps={{
              label: t("Fleet.Settings.BillingEdit.groupHoldAmount"),
              value:
                billing.blocking_amount &&
                billing.group_rides_hold_adjustment &&
                Math.round(
                  billing.blocking_amount *
                    (activeFleet?.max_active_reservations_per_group +
                      Number(billing.group_rides_hold_adjustment)),
                ),
            }}
            unit="currency"
          />
        </GridRow>
        <GridRow>
          <EditFormField
            unit="currency"
            field="min_limit_on_card_no_hold"
            decorationProps={{
              type: "tooltip",
              tooltipText: (
                <Typography color="inherit" align="left" display="inline">
                  {t("Fleet.Settings.Billing.walletToolTipWaiveHold")}
                </Typography>
              ),
            }}
          />
        </GridRow>
      </TitledSection>

      {/* PAYMENT PROVIDERS */}
      <TitledSection title={t("Fleet.Settings.Billing.PaymentProviders")} showDivider={false}>
        {!loading &&
          Object.entries(paymentProviders).map(([name, { commonEnLookup, enabled }]) => (
            <GridRow key={name}>
              <CollapsibleSectionToggle
                label={t(`Fleet.Settings.BillingEdit.${commonEnLookup}`)}
                checked={enabled}
                onToggle={() => {
                  handlePaymentProviderToggle(name);
                }}
                disableToggle={name === "stripe"}
              />
            </GridRow>
          ))}
      </TitledSection>
    </Grid>
  );
};

export default SettingsBillingEdit;
